import { useMemo } from 'react';

import { getEmployerJPState } from '@restworld/utils-common';
import type { JobPosition } from '@restworld/utility-types';

import { isItalianVenue } from '../../libs/salary/is-italian-venue';
import useEmployerJobListings from '../job-listing/hooks/use-employer-job-listing';

/**
 * Fields of a job position needed to assess pay-transparency compliance.
 * Kept narrow so the predicate is easy to reason about and unit test.
 */
type ComplianceRelevantJobPosition = Pick<
  JobPosition,
  | 'published_at'
  | 'status_key'
  | 'salary_type'
  | 'salary_ral'
  | 'salary_ral_min'
  | 'salary_ral_max'
  | 'salary_monthly_gross'
  | 'salary_monthly_gross_min'
  | 'salary_monthly_gross_max'
  | 'salary_hourly_gross'
  | 'salary_hourly_gross_min'
  | 'salary_hourly_gross_max'
>;

// Fetch a generous single page; an organization with more than this many job
// positions is well beyond the at-risk segment this reminder targets.
const ORG_JOB_POSITIONS_PAGE_SIZE = 100;

/**
 * A job position is published/active when it is live for candidates: either it
 * has a `published_at` timestamp, or its status maps to the "open" employer state.
 */
export function isPublishedOrActive(jp: ComplianceRelevantJobPosition): boolean {
  return Boolean(jp.published_at) || getEmployerJPState(jp.status_key) === 'open';
}

/**
 * A job position is compliant with the pay-transparency law (D.Lgs. 96/2026) when
 * a gross figure is shown, which is true if EITHER:
 *  - the legacy `salary_type` is already "gross", OR
 *  - at least one of the new structured gross salary fields is populated.
 * Anything else (net or unspecified, with no structured gross value) is non-compliant.
 *
 * This is the single source of truth for the rule. If the team later changes how
 * compliance is determined, only this function needs to change.
 */
export function isJobPositionNonCompliant(jp: ComplianceRelevantJobPosition): boolean {
  if (jp.salary_type === 'gross') return false;

  const hasStructuredGrossValue = [
    jp.salary_ral,
    jp.salary_ral_min,
    jp.salary_ral_max,
    jp.salary_monthly_gross,
    jp.salary_monthly_gross_min,
    jp.salary_monthly_gross_max,
    jp.salary_hourly_gross,
    jp.salary_hourly_gross_min,
    jp.salary_hourly_gross_max,
  ].some((value) => value !== null && value !== undefined);

  return !hasStructuredGrossValue;
}

interface UseNonCompliantJobPositionsCountOptions {
  enabled?: boolean;
}

/**
 * Counts the current organization's published/active job positions that are not
 * compliant with the pay-transparency law. Reuses the employer job-listing query
 * so it shares the TanStack cache with the listing page when params match.
 */
export function useNonCompliantJobPositionsCount({ enabled = true }: UseNonCompliantJobPositionsCountOptions = {}) {
  const { jobPositionItems, loading } = useEmployerJobListings({
    enabled,
    limit: ORG_JOB_POSITIONS_PAGE_SIZE,
    showPerformanceMetrics: false,
  });

  const count = useMemo(
    () =>
      jobPositionItems.reduce((total, item) => {
        const jp = item.job_position;
        if (!jp) return total;
        // The pay-transparency law (D.Lgs. 96/2026) only applies to Italian venues;
        // foreign venues stay on the legacy flow (same gating as PR #128's salary step).
        const counts =
          isItalianVenue(jp.restaurant?.country) && isPublishedOrActive(jp) && isJobPositionNonCompliant(jp);
        return counts ? total + 1 : total;
      }, 0),
    [jobPositionItems]
  );

  return { count, isLoading: enabled ? loading : false };
}
